import { useCallback } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { HarEntry } from '@terra/shared-schemas-tracker';
import { terraProxyApiClient } from '../../clients/testerClient';

export const TRACKED_HAR_BY_ID_QUERY_KEY = 'tracked-har-by-id';
export const TRACKED_HAR_BY_ID_STALE_TIME = 30_000;
export const TRACKED_HAR_BY_ID_GC_TIME = 60_000;

export const getTrackedHarByIdQueryKey = ({
  applicationId,
  harEntryId,
}: {
  applicationId: string;
  harEntryId: string;
}) => [TRACKED_HAR_BY_ID_QUERY_KEY, applicationId, harEntryId] as const;

export const getTrackedHarById = async ({
  applicationId,
  harEntryId,
}: {
  applicationId: string;
  harEntryId: string;
}): Promise<HarEntry> => {
  const result = await terraProxyApiClient.getHarEntryById({
    applicationId,
    harEntryId,
  });
  return result.harEntry;
};

export const fetchTrackedHarByIdQuery = ({
  queryClient,
  applicationId,
  harEntryId,
}: {
  queryClient: QueryClient;
  applicationId: string;
  harEntryId: string;
}): Promise<HarEntry> =>
  queryClient.fetchQuery({
    queryKey: getTrackedHarByIdQueryKey({
      applicationId,
      harEntryId,
    }),
    queryFn: () =>
      getTrackedHarById({
        applicationId,
        harEntryId,
      }),
    staleTime: TRACKED_HAR_BY_ID_STALE_TIME,
    gcTime: TRACKED_HAR_BY_ID_GC_TIME,
  });

export type TrackedHarFetchRegistry = Map<string, Promise<HarEntry>>;

export const createTrackedHarFetchRegistry = (): TrackedHarFetchRegistry =>
  new Map();

export const fetchTrackedHarByIdWithoutCaching = ({
  queryClient,
  registry,
  applicationId,
  harEntryId,
}: {
  queryClient: QueryClient;
  registry: TrackedHarFetchRegistry;
  applicationId: string;
  harEntryId: string;
}): Promise<HarEntry> => {
  const queryKey = getTrackedHarByIdQueryKey({ applicationId, harEntryId });
  const alreadyRetained = queryClient.getQueryData<HarEntry>(queryKey);
  if (alreadyRetained) {
    return Promise.resolve(alreadyRetained);
  }

  const registryKey = queryKey.join('|');
  const inFlight = registry.get(registryKey);
  if (inFlight) {
    return inFlight;
  }

  const pending = getTrackedHarById({ applicationId, harEntryId }).finally(
    () => {
      registry.delete(registryKey);
    },
  );
  registry.set(registryKey, pending);
  return pending;
};

export const useTrackedHarById = ({
  applicationId,
  harEntryId,
}: {
  applicationId?: string;
  harEntryId?: string | null;
} = {}) => {
  const queryClient = useQueryClient();

  const fetchTrackedHarById = useCallback(
    async ({
      applicationId: fetchApplicationId,
      harEntryId: fetchHarEntryId,
    }: {
      applicationId: string;
      harEntryId: string;
    }): Promise<HarEntry> => {
      return fetchTrackedHarByIdQuery({
        queryClient,
        applicationId: fetchApplicationId,
        harEntryId: fetchHarEntryId,
      });
    },
    [queryClient],
  );

  const query = useQuery({
    queryKey: getTrackedHarByIdQueryKey({
      applicationId: applicationId ?? '',
      harEntryId: harEntryId ?? '',
    }),
    queryFn: () =>
      getTrackedHarById({
        applicationId: applicationId ?? '',
        harEntryId: harEntryId ?? '',
      }),
    enabled: !!applicationId && !!harEntryId,
    staleTime: TRACKED_HAR_BY_ID_STALE_TIME,
    gcTime: TRACKED_HAR_BY_ID_GC_TIME,
  });

  return {
    fetchTrackedHarById,
    harEntry: query.data ?? null,
    isLoading: query.isPending || query.isFetching,
    isError: query.isError,
  };
};
